/**
 * @file: util.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
import { StatusColors } from '@euler/components/Colors';
import {
  formatCarwashType,
  formatOldPartDisposableType,
  formatReservationType,
  formatReturnType,
  kDefaultDateFormat,
  kDefaultDatetimeFormat,
} from '@euler/functions/order';
import { kOrderFieldNoValuePlaceHolder } from '@euler/functions/order/constants';
import { useStoreConfiguration } from '@euler/functions/useStoreConfiguration';
import { useUserPermissions } from '@euler/functions/user/useUserPermissions';
import {
  ExtendedProperty,
  ExtendedPropertyDef,
  OrderFieldVisibility,
  OrderFieldsConfig,
  OrderSummaryFieldsConfig,
} from '@euler/model';
import {
  CarwashType,
  OldPartDisposalType,
  OrderReservationType,
  OrderReturnType,
} from '@euler/model/enum';
import { comparer, isNotNull, onErrorReturn } from '@euler/utils';
import { maybeDateFromValue } from '@euler/utils/datetime';
import { formatDate } from '@euler/utils/formatters';
import { withErrorHandler } from '@euler/utils/withErrorHandler';
import format from 'date-fns/format';
import { useCallback, useMemo } from 'react';
import { ExtendedPropSummaryItem, OrderSummaryCardProps } from './types';

export const kCustomerSignedSummaryItemKey = '__customer-signed__';

export type ShouldDisplayFieldOptions<T> = {
  value: T;
  visibility?: OrderFieldVisibility;
  valid?: (value: T) => boolean;
  defaultVisible?: boolean;
};

export const shouldDisplayField = <T>({
  value,
  visibility,
  valid,
  defaultVisible,
}: ShouldDisplayFieldOptions<T>) => {
  if (visibility === 'none') {
    return false;
  }

  let hasValue = valid?.(value);
  if (hasValue == null) {
    if (typeof value === 'string') {
      hasValue = value.trim().length > 0;
    } else if (typeof value === 'number') {
      hasValue = !isNaN(value);
    } else if (Array.isArray(value)) {
      hasValue = value.length > 0;
    } else {
      hasValue = value != null;
    }
  }
  const isVisible =
    visibility === 'always' ||
    (visibility === 'auto' && hasValue) ||
    ((visibility === 'default' || visibility == null) &&
      (defaultVisible === true || (defaultVisible == null && hasValue)));

  return isVisible;
};

export const getExtendedPropDetail = (
  prop: ExtendedProperty,
  def: ExtendedPropertyDef,
) => {
  let value: any | undefined = undefined;
  let text: string | undefined = undefined;
  let textSegments: { text: string; color?: string }[] | undefined = undefined;
  let color = def.color;

  switch (def.type) {
    case 'text': {
      value = typeof prop.value === 'string' ? prop.value : undefined;
      text = value;
      break;
    }

    case 'switch': {
      value = typeof prop.value === 'boolean' ? prop.value : undefined;
      text =
        value === true
          ? def.yesLabel?.trim() || '是'
          : value === false
          ? def.noLabel?.trim() || '否'
          : undefined;
      color =
        value === true
          ? def.yesColor || undefined
          : value === false
          ? def.noColor || undefined
          : undefined;
      break;
    }

    case 'datetime': {
      if (
        prop.value != null &&
        (typeof prop.value === 'string' || prop.value instanceof Date)
      ) {
        const dateValue = maybeDateFromValue(prop.value);
        if (dateValue != null) {
          value = dateValue;
          const defaultFormat = def.dateOnly
            ? kDefaultDateFormat
            : kDefaultDatetimeFormat;
          // silence error caused by invalid format string and return the
          // date string formatted with the default format.
          text = withErrorHandler(
            () => format(dateValue, def.displayFormat?.trim() || defaultFormat),
            onErrorReturn(() => format(dateValue, defaultFormat)),
          ) as string | undefined;
        }
      }
      break;
    }

    case 'select': {
      if (prop.value != null && typeof prop.value !== 'boolean') {
        const values =
          typeof prop.value === 'string' ? [prop.value] : prop.value;

        const labels = values
          .map(v => {
            const option = def.options.find(
              x => (x.value ?? x.defaultValue) === v,
            );
            return option?.label
              ? { text: option?.label, color: option?.color ?? def.color }
              : null;
          })
          .filter(isNotNull);

        value = values;
        text = labels.join('/');
        textSegments = labels;
      }
      break;
    }

    case 'assets': {
      if (prop.extra?.type === 'assets' && prop.extra?.content?.length) {
        value = prop.value;
        text = `${prop.extra.content.length}个`;
      }
      break;
    }

    default:
      break;
  }

  return { value, text, textSegments, color };
};

export const useOrderSummary = (props: OrderSummaryCardProps) => {
  const userPermissions = useUserPermissions();
  const storeConfiguration = useStoreConfiguration();
  const { orderDetail } = props;

  const $visibility = useCallback(
    (field: keyof OrderFieldsConfig, defaultValue?: OrderFieldVisibility) => {
      const fields = storeConfiguration?.app?.orderFlow?.fields;
      return fields?.[`${field}`]?.includeInOrderSummary ?? defaultValue;
    },
    [storeConfiguration?.app?.orderFlow?.fields],
  );

  const $summaryVisibility = useCallback(
    (field: keyof OrderSummaryFieldsConfig) => {
      const fields = storeConfiguration?.app?.orderSummary?.fields;
      return fields?.[`${field}`]?.visibility;
    },
    [storeConfiguration?.app?.orderSummary?.fields],
  );

  const defaultVisibility =
    storeConfiguration?.app?.orderSummary?.defaultExtendedPropsVisibility;

  const extraItems = useExtendedPropsExtraSummaryItemsInCompactMode(
    props,
    $visibility,
  );

  const extendedPropItems = useMemo(() => {
    if (props.hideExtendedProps) {
      return [];
    }
    const res: ExtendedPropSummaryItem[] = [...(extraItems ?? [])];
    if (orderDetail?.extendedProps?.length) {
      for (const prop of orderDetail.extendedProps) {
        const latestDef = storeConfiguration?.extendedOrderProperties?.find(
          x => x.id === prop.id,
        );
        const def = orderDetail.extendedPropDefs?.find(x => x.id === prop.id);
        if (def != null) {
          const { value, text, textSegments, color } = getExtendedPropDetail(
            prop,
            def,
          );
          const isVisible = shouldDisplayField({
            value,
            visibility:
              latestDef?.includeInOrderSummary ??
              def.includeInOrderSummary ??
              defaultVisibility,
            defaultVisible:
              defaultVisibility == null || defaultVisibility === 'default'
                ? false
                : undefined,
          });
          if (isVisible) {
            const s = textSegments?.length
              ? textSegments.map(seg => seg.text).join(' ')
              : text ?? '';
            res.push({
              id: prop.id,
              label: def.name,
              value,
              text,
              textSegments,
              textLength: Math.max(s.length, def.name.length),
              remark: prop.remark,
              color,
            });
          }
        }
      }
    }
    if (res.length) {
      res.push({
        id: kCustomerSignedSummaryItemKey,
        label: '客户签字',
        value: props.customerSigned ? '已签字' : '未签字',
        text: props.customerSigned ? '已签字' : '未签字',
        textLength: 4,
        textStyle: {
          color: props.customerSigned
            ? StatusColors.Success
            : StatusColors.Danger,
        },
      });
    }

    res.sort((x, y) => comparer(x.textLength, y.textLength));
    return res;
  }, [
    props.hideExtendedProps,
    props.customerSigned,
    extraItems,
    orderDetail?.extendedProps,
    orderDetail?.extendedPropDefs,
    storeConfiguration?.extendedOrderProperties,
    defaultVisibility,
  ]);

  return {
    $visibility,
    $summaryVisibility,
    userPermissions,
    storeConfiguration,
    extendedPropItems,
  };
};
export const useExtendedPropsExtraSummaryItemsInCompactMode = (
  props: OrderSummaryCardProps,
  $visibility: (
    field: keyof OrderFieldsConfig,
  ) => OrderFieldVisibility | undefined,
) => {
  return useMemo<ExtendedPropSummaryItem[]>(() => {
    const buildItem = <T>({
      id,
      label,
      format: _format,
      ...options
    }: ShouldDisplayFieldOptions<T> & {
      id: string;
      label: string;
      format?: (value: T) => string;
    }): ExtendedPropSummaryItem | undefined => {
      const visible = shouldDisplayField(options);
      if (!visible) return undefined;
      const text = _format?.(options.value) ?? String(options.value);
      return {
        id,
        label,
        value: options.value,
        text,
        textLength: Math.max(label.length, text.length),
      };
    };

    const returnTypeItem = buildItem({
      id: '__return-type__',
      label: '车辆归还方式',
      value: props.returnType,
      visibility: $visibility('returnType'),
      defaultVisible:
        props.returnType != null && props.returnType !== OrderReturnType.None,
      format: x => formatReturnType(x, kOrderFieldNoValuePlaceHolder),
    });

    const carwashTypeItem = buildItem({
      id: '__carwash-type__',
      label: '是否需要洗车',
      value: props.carwashType,
      visibility: $visibility('carwashType'),
      defaultVisible:
        props.carwashType != null && props.carwashType !== CarwashType.None,
      format: x => formatCarwashType(x, kOrderFieldNoValuePlaceHolder),
    });

    const oldPartDisposalTypeItem = buildItem({
      id: '__old-part-disposal-type__',
      label: '旧件处理方式',
      value: props.oldPartDisposalType,
      visibility: $visibility('oldPartDisposalType'),
      defaultVisible:
        props.oldPartDisposalType != null &&
        props.oldPartDisposalType !== OldPartDisposalType.None,
      format: x =>
        formatOldPartDisposableType(x, kOrderFieldNoValuePlaceHolder),
    });

    const reservationTypeItem = buildItem({
      id: '__reservation-type__',
      label: '客户是否预约',
      value: props.reservationType,
      visibility: $visibility('reservationType'),
      defaultVisible:
        props.reservationType != null &&
        props.reservationType !== OrderReservationType.None,
      format: x => formatReservationType(x, kOrderFieldNoValuePlaceHolder),
    });

    const insuranceExpiryDateItem = buildItem({
      id: '__insurance-expiry-date__',
      label: '保险到期日期',
      value: props.insuranceExpiryDate,
      visibility: $visibility('insuranceExpiryDate'),
      format: x => (x ? formatDate(x) : '-'),
    });

    return [
      returnTypeItem,
      carwashTypeItem,
      oldPartDisposalTypeItem,
      reservationTypeItem,
      insuranceExpiryDateItem,
    ].filter(isNotNull);
  }, [
    $visibility,
    props.carwashType,
    props.insuranceExpiryDate,
    props.oldPartDisposalType,
    props.reservationType,
    props.returnType,
  ]);
};
